import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Brain,
  Sparkles,
  Target,
  Music,
  ArrowRight,
  Check,
  ShieldCheck,
  Calendar,
  Headphones,
  GraduationCap,
  Mail,
  ExternalLink,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PrivacyPolicy from '../components/PrivacyPolicy';
import SimplifiedPricing from '../components/SimplifiedPricing';
import { Button } from '../components/ui/button';
import { CALENDLY_URL, LOGO_URL, INSTAGRAM_URL } from '../lib/siteConfig';
import { useDocumentHead } from './blog/useDocumentHead';

/**
 * /pricing — Public, transparent pricing hub.
 *
 * Single canonical destination for every "what does it cost?" CTA across the
 * marketing site. Consolidates four offer types into one scannable page:
 *
 *   1. Free entry points (Quiz, Playbooks)
 *   2. Self-serve premium products (Apex Six IDP $27, AI Coach Pro $19.99/mo,
 *      Locker Room Pass $29.99/mo)
 *   3. 1:1 coaching with Coach Lewis (SimplifiedPricing component — per
 *      session packages: Individual $175/session, Parent/Coach $175/session,
 *      Teams & Schools — Scoped on a Planning Call.)
 *   4. Schools, clubs, and team workshops (deep link to /schools + /workshops)
 *
 * Subscription prices are sourced from backend PRICING_PLANS in server.py and
 * must be kept in sync — if you change a price there, mirror it here.
 */

// Subscription price snapshots — MUST mirror backend PRICING_PLANS in
// /app/backend/server.py. Update both together.
const SUBSCRIPTION_PRICES = {
  ai_coach_monthly: { display: '$19.99', period: '/mo' },
  ai_coach_yearly: { display: '$199', period: '/yr', perMonth: '$16.58/mo' },
  locker_room_monthly: { display: '$29.99', period: '/mo' },
  locker_room_yearly: { display: '$299', period: '/yr', perMonth: '$24.92/mo' },
};

const FREE_TIERS = [
  {
    icon: Brain,
    title: 'The Mindset Quiz',
    badge: 'Free · 2 minutes',
    description:
      'Score yourself across all six dimensions of the Apex Six framework. Get your Primary Action — the one mental skill to install first.',
    bullets: ['No credit card', 'Instant results', 'Personalized track'],
    ctaLabel: 'Start the quiz',
    ctaHref: '/quiz',
    testId: 'pricing-tier-quiz',
  },
  {
    icon: Mail,
    title: 'Audience Playbooks',
    badge: 'Free · PDF download',
    description:
      'Free playbooks tuned to your audience. The Car-Ride-Home Playbook for parents, the Coach\u2019s Reset for coaches, and the Pre-Game Mental Performance Checklist for athletes.',
    bullets: ['Athlete · Parent · Coach versions', 'Instant email delivery', 'Drip series included'],
    ctaLabel: 'Browse free resources',
    ctaHref: '/playbook',
    testId: 'pricing-tier-playbooks',
  },
];

const SELF_SERVE_TIERS = [
  {
    icon: Target,
    title: 'Apex Six IDP',
    priceDisplay: '$27',
    pricePeriod: 'one-time',
    tagline: 'Your scored profile + a printable plan.',
    description:
      'The premium 8-minute athlete self-assessment. Scored across all six dimensions, with your Primary Action, three rehearsable drills for this week, and a printable Individual Development Plan you can pin in your locker.',
    bullets: [
      'Scored profile across all 6 dimensions',
      'Your Primary Action (what to install first)',
      'Printable IDP — pin it in the locker',
      '25% off your first 1:1 session',
    ],
    ctaLabel: 'Start the $27 IDP',
    ctaHref: '/assessment',
    testId: 'pricing-tier-idp',
    popular: false,
  },
  {
    icon: Sparkles,
    title: 'Apex AI Coach Pro',
    priceDisplay: SUBSCRIPTION_PRICES.ai_coach_monthly.display,
    pricePeriod: SUBSCRIPTION_PRICES.ai_coach_monthly.period,
    yearlyOption: `or ${SUBSCRIPTION_PRICES.ai_coach_yearly.display}${SUBSCRIPTION_PRICES.ai_coach_yearly.period} (${SUBSCRIPTION_PRICES.ai_coach_yearly.perMonth})`,
    tagline: 'In-pocket access to Coach Lewis\u2019s method.',
    description:
      'A private AI coach trained on the Apex Six method. Pre-game prep plans, journal reflections, and on-demand mental performance coaching between sessions. Free version available too \u2014 Pro unlocks 10x daily usage and saved history.',
    bullets: [
      '50 AI Coach messages/day',
      '15 Pre-Game plans/day',
      '25 Journal reflections/day',
      'Full chat & plan history saved',
      '7-day free trial \u2014 no card required',
    ],
    ctaLabel: 'Start 7-day free trial',
    ctaHref: '/app/login',
    ctaTrack: 'AI Coach Pro',
    testId: 'pricing-tier-ai-coach',
    popular: true,
  },
  {
    icon: Music,
    title: 'The Locker Room Pass',
    priceDisplay: SUBSCRIPTION_PRICES.locker_room_monthly.display,
    pricePeriod: SUBSCRIPTION_PRICES.locker_room_monthly.period,
    yearlyOption: `or ${SUBSCRIPTION_PRICES.locker_room_yearly.display}${SUBSCRIPTION_PRICES.locker_room_yearly.period} (${SUBSCRIPTION_PRICES.locker_room_yearly.perMonth})`,
    tagline: 'The members-only audio + video library.',
    description:
      'Pre-game playlists, the Apex Six audio notecards, Coach Lewis\u2019s recorded sessions, and the full bonus library. Cancel anytime; first preview is free.',
    bullets: [
      'Curated pre-game playlists',
      'Apex Six audio notecards',
      'Bonus video library + recorded sessions',
      'New drops every month',
    ],
    ctaLabel: 'Preview the Locker Room',
    ctaHref: '/locker-room',
    testId: 'pricing-tier-locker-room',
    popular: false,
  },
];

function trackPricingCta(label) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'pricing_cta_click', { label });
    }
  } catch {
    /* analytics is best-effort */
  }
}

export default function PricingPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);

  useDocumentHead({
    title: 'Pricing \u2014 Apex Athletic Mind',
    description:
      'Transparent pricing for every Apex Athletic Mind product. Free Mindset Quiz, $27 Apex Six IDP, $19.99/mo AI Coach Pro, $29.99/mo Locker Room Pass, and 1:1 coaching with Coach Lewis from $175/session. Volume discounts for teams and schools.',
    canonical: '/pricing',
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'PriceSpecification',
      name: 'Apex Athletic Mind \u2014 Pricing',
      url: 'https://apexathleticmind.com/pricing',
      priceCurrency: 'USD',
      offers: [
        {
          '@type': 'Offer',
          name: 'Mindset Quiz',
          price: '0',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: 'Apex Six IDP',
          price: '27',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: 'Apex AI Coach Pro \u2014 Monthly',
          price: '19.99',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: 'The Locker Room Pass \u2014 Monthly',
          price: '29.99',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          name: '1:1 Coaching with Coach Lewis',
          price: '175',
          priceCurrency: 'USD',
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-background text-foreground pb-[72px] md:pb-0" data-testid="pricing-page">
      <Navbar calendlyLink={CALENDLY_URL} logoUrl={LOGO_URL} />

      <main>
        {/* HERO */}
        <section
          className="relative bg-gradient-to-b from-[#0a0d0e] via-[#0a0d0e] to-[#0F1517] text-white pt-28 sm:pt-36 pb-16 sm:pb-20"
          data-testid="pricing-hero"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full bg-[#4FBCC7]/8 blur-3xl"
          />
          <div className="relative max-w-[1120px] mx-auto px-5 sm:px-8 text-center">
            <div className="inline-flex items-center gap-1.5 font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#4FBCC7] mb-4">
              <ShieldCheck className="h-3 w-3" />
              Transparent Pricing
            </div>
            <h1
              className="font-heading text-[2.3rem] sm:text-[3.2rem] font-bold tracking-tight leading-[1.05] mb-5 max-w-[20ch] mx-auto"
              data-testid="pricing-hero-title"
            >
              Every product. Every price. One page.
            </h1>
            <p className="text-[16.5px] sm:text-[18px] text-white/72 leading-[1.6] max-w-[58ch] mx-auto mb-7">
              No quotes-on-request. No hidden tiers. Pick the door that fits where you are
              right now &mdash; from a free 2-minute quiz to ongoing 1:1 work with Coach Lewis. Programs for schools and clubs are scoped individually — see the section below and book a Planning Call.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <Button
                asChild
                className="bg-[#4FBCC7] hover:bg-[#3FA9B5] text-[#0a0d0e] h-11 px-5 font-bold"
                data-testid="pricing-hero-cta-quiz"
              >
                <Link to="/quiz" onClick={() => trackPricingCta('Hero · Quiz')}>
                  Start with the Free Quiz
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white/30 bg-white/5 text-white hover:bg-white/10 h-11 px-5 font-semibold"
                data-testid="pricing-hero-cta-discovery"
              >
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackPricingCta('Hero · Discovery Call')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Book a Discovery Call
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* SECTION 1 — FREE TIERS */}
        <section className="py-16 sm:py-20 bg-white" data-testid="pricing-section-free">
          <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
            <div className="mb-10">
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-3">
                01 · Start Free
              </div>
              <h2 className="font-heading text-[1.7rem] sm:text-[2.1rem] font-bold tracking-tight text-[#003a40] mb-3 leading-[1.15]">
                Two ways in. Zero dollars.
              </h2>
              <p className="text-[15.5px] text-[#1a1a1a]/70 max-w-[62ch] leading-[1.65]">
                Every athlete, parent, and coach starts here. The Mindset Quiz and the free
                Playbooks are the on-ramps to everything else.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
              {FREE_TIERS.map((tier) => {
                const Icon = tier.icon;
                return (
                  <div
                    key={tier.title}
                    className="rounded-2xl border border-[#004C54]/15 bg-[#F5F3EE]/40 p-6 sm:p-7 hover:border-[#004C54]/30 hover:bg-[#F5F3EE]/70 transition-colors flex flex-col"
                    data-testid={tier.testId}
                  >
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#004C54] text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="font-mono-accent uppercase tracking-[0.18em] text-[10px] font-bold text-[#004C54]/80 bg-[#4FBCC7]/15 rounded-full px-3 py-1">
                        {tier.badge}
                      </span>
                    </div>
                    <h3 className="font-heading text-[1.25rem] sm:text-[1.4rem] font-bold text-[#003a40] mb-2">
                      {tier.title}
                    </h3>
                    <p className="text-[14.5px] text-[#3a3a3a] leading-[1.6] mb-4">{tier.description}</p>
                    <ul className="space-y-2 mb-5 flex-1">
                      {tier.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-[13.5px] text-[#1a1a1a]/75">
                          <Check className="h-4 w-4 text-[#004C54] mt-0.5 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      asChild
                      className="bg-[#004C54] hover:bg-[#003a40] text-white h-11 font-semibold w-full"
                      data-testid={`${tier.testId}-cta`}
                    >
                      <Link to={tier.ctaHref} onClick={() => trackPricingCta(tier.title)}>
                        {tier.ctaLabel}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 2 — SELF-SERVE PREMIUM */}
        <section className="py-16 sm:py-20 bg-[#F5F3EE]/50 border-y border-[#004C54]/10" data-testid="pricing-section-self-serve">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            <div className="mb-10">
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-3">
                02 · Self-Serve Products
              </div>
              <h2 className="font-heading text-[1.7rem] sm:text-[2.1rem] font-bold tracking-tight text-[#003a40] mb-3 leading-[1.15]">
                Train your mind on your own schedule.
              </h2>
              <p className="text-[15.5px] text-[#1a1a1a]/70 max-w-[62ch] leading-[1.65]">
                Three premium products that work without a calendar. The $27 IDP for athletes
                ready to commit, plus two subscriptions that put Coach Lewis’s method in
                your pocket and your headphones.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6 lg:gap-7 lg:items-stretch">
              {SELF_SERVE_TIERS.map((tier) => {
                const Icon = tier.icon;
                return (
                  <div
                    key={tier.title}
                    className={`relative rounded-2xl bg-white p-7 flex flex-col ${
                      tier.popular
                        ? 'ring-2 ring-[#004C54] shadow-[0_18px_50px_-12px_rgba(0,76,84,0.30)] lg:scale-[1.02] lg:z-10'
                        : 'ring-1 ring-[#004C54]/15 shadow-sm'
                    }`}
                    data-testid={tier.testId}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-[#004C54] text-white text-[11px] font-bold uppercase tracking-[0.16em] px-3.5 py-1 rounded-full font-mono-accent">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#004C54] text-white mb-4">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-heading text-[1.4rem] font-bold text-[#003a40] mb-1.5">
                      {tier.title}
                    </h3>
                    <p className="text-[13.5px] text-[#1a1a1a]/65 mb-4 leading-snug">{tier.tagline}</p>

                    <div className="flex items-baseline gap-1.5 mb-1">
                      <span className="font-heading text-[2.2rem] font-bold text-[#004C54] tracking-tight">
                        {tier.priceDisplay}
                      </span>
                      <span className="text-[14px] text-[#1a1a1a]/55 font-medium">{tier.pricePeriod}</span>
                    </div>
                    {tier.yearlyOption && (
                      <p className="text-[12px] text-[#1a1a1a]/55 mb-5">{tier.yearlyOption}</p>
                    )}
                    {!tier.yearlyOption && <div className="mb-5" />}

                    <p className="text-[14px] text-[#3a3a3a] leading-[1.6] mb-4">{tier.description}</p>

                    <ul className="space-y-2 mb-6 flex-1">
                      {tier.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-[13.5px] text-[#1a1a1a]/80">
                          <Check className="h-4 w-4 text-[#004C54] mt-0.5 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      asChild
                      className={`w-full h-11 font-semibold ${
                        tier.popular
                          ? 'bg-[#004C54] hover:bg-[#003a40] text-white'
                          : 'bg-white border-2 border-[#004C54] text-[#004C54] hover:bg-[#004C54] hover:text-white'
                      }`}
                      data-testid={`${tier.testId}-cta`}
                    >
                      <Link
                        to={tier.ctaHref}
                        onClick={() => trackPricingCta(tier.ctaTrack || tier.title)}
                      >
                        {tier.ctaLabel}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Sub-row link to in-app pricing for granular comparison */}
            <div className="mt-7 text-center">
              <Link
                to="/app/login"
                onClick={() => trackPricingCta('Compare subscription plans')}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#004C54] underline decoration-[#4FBCC7]/50 decoration-2 underline-offset-2 hover:decoration-[#004C54]"
                data-testid="pricing-compare-plans-link"
              >
                Compare every subscription tier &amp; the free plan in the app
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </section>

        {/* SECTION 3 — 1:1 COACHING (SimplifiedPricing) */}
        <section data-testid="pricing-section-1-on-1">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-16 sm:pt-20 pb-2">
            <div>
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-3">
                03 \u00b7 Work With Coach Lewis 1:1
              </div>
              <h2 className="font-heading text-[1.7rem] sm:text-[2.1rem] font-bold tracking-tight text-[#003a40] mb-3 leading-[1.15]">
                Live sessions. Personal coaching.
              </h2>
              <p className="text-[15.5px] text-[#1a1a1a]/70 max-w-[62ch] leading-[1.65]">
                Three clear paths to work with Coach Lewis directly &mdash; for the athlete, the
                people around them, and the teams they play for. Every multi-session package
                includes the Apex AI Coach app between sessions.
              </p>
            </div>
          </div>
          {/* SimplifiedPricing renders its own section wrapper + bottom CTA */}
          <SimplifiedPricing calendlyLink={CALENDLY_URL} />
        </section>

        {/* SECTION 4 — SCHOOLS & TEAMS */}
        <section className="py-16 sm:py-20 bg-[#0a0d0e] text-white" data-testid="pricing-section-schools">
          <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
            <div className="grid sm:grid-cols-[1fr_1.3fr] gap-8 sm:gap-12 items-center">
              <div>
                <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#4FBCC7] mb-3">
                  04 · For Schools, Clubs &amp; Teams
                </div>
                <h2 className="font-heading text-[1.65rem] sm:text-[2rem] font-bold tracking-tight mb-3 leading-[1.15]">
                  Workshops, season-long curriculum, and program consulting.
                </h2>
                <p className="text-[15px] text-white/72 leading-[1.65] mb-5">
                  Athletic directors, school administrators, and club leadership building real
                  mental skills across an entire roster &mdash; not just the captains. Programs
                  are scoped on <strong className="text-white font-semibold">a Planning Call</strong>, because the right build depends on roster size, season calendar and how much your own coaches will deliver.
                </p>
                <div className="flex flex-wrap gap-2.5">
                  <Button
                    asChild
                    className="bg-[#4FBCC7] hover:bg-[#3FA9B5] text-[#0a0d0e] h-11 px-5 font-bold"
                    data-testid="pricing-schools-cta-plan"
                  >
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => trackPricingCta('Schools · Planning Call')}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Book a Planning Call
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-2 border-white/30 bg-white/5 text-white hover:bg-white/15 h-11 px-5 font-semibold"
                    data-testid="pricing-schools-cta-workshops"
                  >
                    <Link to="/workshops" onClick={() => trackPricingCta('Schools · Workshops')}>
                      See Workshop Catalog
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4">
                  <GraduationCap className="h-5 w-5 text-[#4FBCC7] mb-2" />
                  <div className="font-heading text-[15px] font-bold mb-1">Single workshop</div>
                  <div className="text-[12.5px] text-white/65 leading-snug">
                    A 60–90 minute session for a team or staff. Scoped on a Planning Call.
                  </div>
                </div>
                <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4">
                  <Target className="h-5 w-5 text-[#4FBCC7] mb-2" />
                  <div className="font-heading text-[15px] font-bold mb-1">Multi-session package</div>
                  <div className="text-[12.5px] text-white/65 leading-snug">
                    Three to six sessions across a season.
                  </div>
                </div>
                <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4">
                  <Headphones className="h-5 w-5 text-[#4FBCC7] mb-2" />
                  <div className="font-heading text-[15px] font-bold mb-1">Year-long program</div>
                  <div className="text-[12.5px] text-white/65 leading-snug">
                    Apex Six curriculum, coach certification, and Apex-led touchpoints. Priced per site.
                  </div>
                </div>
                <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-4">
                  <ShieldCheck className="h-5 w-5 text-[#4FBCC7] mb-2" />
                  <div className="font-heading text-[15px] font-bold mb-1">Program consulting</div>
                  <div className="text-[12.5px] text-white/65 leading-snug">
                    For ADs &amp; club leadership. Custom scope.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA */}
        <section className="py-16 sm:py-20 bg-white" data-testid="pricing-closing-cta">
          <div className="max-w-[820px] mx-auto px-5 sm:px-8 text-center">
            <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold text-[#004C54] mb-3">
              Not sure which fits?
            </div>
            <h2 className="font-heading text-[1.7rem] sm:text-[2.1rem] font-bold tracking-tight text-[#003a40] mb-4 leading-[1.15]">
              Tell me your scenario. I’ll tell you the door.
            </h2>
            <p className="text-[15.5px] text-[#1a1a1a]/70 leading-[1.65] max-w-[58ch] mx-auto mb-7">
              A 30-minute Discovery Call is free and no-pressure. Bring the moment that’s
              been on your mind &mdash; we’ll map the plan that fits your athlete, your
              budget, and your season.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <Button
                asChild
                className="bg-[#004C54] hover:bg-[#003a40] text-white h-11 px-5 font-semibold"
                data-testid="pricing-closing-cta-discovery"
              >
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackPricingCta('Closing · Discovery Call')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Book a Discovery Call
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-[#004C54] text-[#004C54] hover:bg-[#004C54]/8 h-11 px-5 font-semibold"
                data-testid="pricing-closing-cta-contact"
              >
                <Link to="/contact" onClick={() => trackPricingCta('Closing · Front Desk')}>
                  Call the Front Desk
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer
        calendlyLink={CALENDLY_URL}
        instagramLink={INSTAGRAM_URL}
        logoUrl={LOGO_URL}
        onPrivacyClick={() => setPrivacyOpen(true)}
      />
      <PrivacyPolicy open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}
